import React, { useCallback, useState, useMemo, useContext } from 'react';

import TimePicker, { TimePickerType } from 'app/home/general-components/TouchPicker/TimePicker';
import { BindActionType, BindConditionType } from 'app/model/Bind';
import { isValidMobilePhoneNumber, toEnglishNumber } from 'app/home/general-components/NumberInputHelper';
import { LocalizationContext } from 'app/localization/Context';
import { createBindAlarmInfoObject, createBindCallInfoObject, createBindEmailInfoObject, createBindSMSInfoObject } from 'app/model/BindData';

import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconBack } from 'images/general/back.svg';

import { ReactComponent as IconBackBlack } from 'images/general/back-black.svg';
import { ReactComponent as IconCall } from 'images/bind/element/call-white.svg';
import { ReactComponent as IconEmail } from 'images/bind/element/email-white.svg';
import { ReactComponent as IconSms } from 'images/bind/element/sms-white.svg';

import './SpecialTypes.scss'
import PhoneNumberInput from 'app/home/general-components/PhoneNumberInput/PhoneNumberInput';


export default function SpecialTypePage(props) {
    switch (props.pageData.eventType) {
        case 'alarm':
            return React.createElement(Alarm, props);
        case 'email':
        case 'call':
        case 'sms':
            return React.createElement(CallEmailSMS, props);
        default:
            return null;
    }
}

function timeToHHmm(h, m) {
    let t = "";
    if (h < 10) {
        t += "0" + String(h);
    } else {
        t += String(h);
    }
    t += ":"
    if (m < 10) {
        t += "0" + String(m);
    } else {
        t += String(m);
    }
    return t;
}

function Alarm({ preBind, pushState, back, dismissHandler }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    // useable only when page appears
    let _initialAlarmValue = "00:00";
    let _initialAlarmValueHour = 0;
    let _initialAlarmValueMin = 0;
    const _lastSpecialCondInfos = preBind.conditionInfos.filter(condInfo => (condInfo.data.condition_type === BindConditionType.alarm));
    if (_lastSpecialCondInfos.length > 0) {
        _initialAlarmValue = _lastSpecialCondInfos[0].data["time"];
        _initialAlarmValueHour = parseInt(_initialAlarmValue.split(':')[0]);
        _initialAlarmValueMin = parseInt(_initialAlarmValue.split(':')[1]);
    }

    const [time, setTime] = useState(_initialAlarmValue);

    const timeSelectedCallback = useCallback((h, m, s) => {
        setTime(timeToHHmm(h, m));
    }, []);

    const nextClick = useCallback(() => {
        const _preBind = { ...preBind };
        let _conditionInfos = [..._preBind.conditionInfos];
        _conditionInfos = _conditionInfos.filter(condInfo => (
            (condInfo.data.condition_type !== BindConditionType.alarm) ||
            (condInfo.data.time !== time)
        ));
        _conditionInfos.push(createBindAlarmInfoObject(time, l));
        _preBind.conditionInfos = _conditionInfos;
        pushState({ preBind: _preBind, pageData: { name: 'main' } });
    }, [time, preBind, pushState, l]);

    return (
        <div className="create-bind-page-content">
            <IconBack className="nav-icon" /> <div className="nav-button" onClick={() => { back() }} />
            <IconX className="nav-icon-secondary" /> <div className="nav-button-secondary" onClick={dismissHandler} />

            <div className="header">{b.if}</div>
            <div className="info" style={{ marginBottom: 8 }}>{b.whenTheClockShowsTheseNumbers}</div>

            <div className="alarm-picker-container">
                <TimePicker
                    pickerName="alarm_time_picker" type={TimePickerType.hour_minute}
                    height={231} eachPickerWidth={77} baseFontSize={64} color="#ffffff"
                    defaultHours={_initialAlarmValueHour} defaultMinutes={_initialAlarmValueMin} defaultseconds={0}
                    timeSelectedCallback={timeSelectedCallback} />
            </div>

            <div className="big-button blue-bottom bottom-next-button" onClick={nextClick}>
                <div>{b.next}</div> <IconBackBlack className="icon-180" />
            </div>
        </div>
    );
}

function isEmail(str) {
    // https://emailregex.com
    // eslint-disable-next-line
    const pattern = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    return pattern.test(str);
}

function CallEmailSMS({ preBind, pushState, pageData, back, dismissHandler }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    const [value, setValue] = useState("");

    // for sms
    const [_countryCode, _setCountryCode] = useState('');
    const [_phoneNumber, _setPhoneNumber] = useState('');

    const nextClick = useCallback(() => {
        const _preBind = { ...preBind };
        let _actionInfos = [..._preBind.actionInfos];

        switch (pageData.eventType) {
            case "email":
                if (value.length > 0 && isEmail(value)) {
                    _actionInfos = _actionInfos.filter(info =>
                        (info.data.action_type !== BindActionType.email) ||
                        (info.data.email !== value)
                    );
                    _actionInfos.push(createBindEmailInfoObject(value, l));
                    _preBind.actionInfos = _actionInfos;
                    pushState({ preBind: _preBind, pageData: { name: 'main' } });
                }
                break;
            case "sms":
                if (isValidMobilePhoneNumber(_countryCode, _phoneNumber)) {
                    const num = "+" + _countryCode + _phoneNumber;
                    _actionInfos = _actionInfos.filter(info =>
                        (info.data.action_type !== BindActionType.sms) ||
                        (info.data.phone_number !== num)
                    );
                    _actionInfos.push(createBindSMSInfoObject(num, l));
                    _preBind.actionInfos = _actionInfos;
                    pushState({ preBind: _preBind, pageData: { name: 'main' } });
                }
                break;
            case "call":
                if (value.length > 0) {
                    _actionInfos = _actionInfos.filter(info =>
                        (info.data.action_type !== BindActionType.call) ||
                        (info.data.phone_number !== value)
                    );
                    _actionInfos.push(createBindCallInfoObject(value, l));
                    _preBind.actionInfos = _actionInfos;
                    pushState({ preBind: _preBind, pageData: { name: 'main' } });
                }
                break;
            default:
                break;
        }
    }, [value, _countryCode, _phoneNumber, preBind, pushState, pageData.eventType, l]);

    const [header, headerIcon, info, placeholder] = useMemo(() => {
        switch (pageData.eventType) {
            case "email":
                return [b.email, IconEmail, b.chosseAddress, "example@gmail.com"]
            case "sms":
                return [b.sms, IconSms, b.chosseNumber, ""]
            case "call":
                return [b.call, IconCall, b.chosseNumber, b.phonenumber]
            default:
                return null;
        }
    }, [pageData.eventType, b.email, b.sms, b.call, b.chosseAddress, b.chosseNumber, b.phonenumber]);

    const [needToShowHelpText, helpText] = useMemo(() => {
        switch (pageData.eventType) {
            case "email":
                return [!(value.length > 0 && isEmail(value)), b.youMustEnterAnAddress]
            case "sms":
                return [!isValidMobilePhoneNumber(_countryCode, _phoneNumber), b.youMustEnterANumber]
            case "call":
                return [!(value.length > 0), b.youMustEnterANumber]
            default:
                return null;
        }
    }, [pageData.eventType, value, _countryCode, _phoneNumber,
    b.youMustEnterANumber, b.youMustEnterAnAddress]);

    const onSMSInputChanged = useCallback((countryCode, phoneNumber) => {
        _setCountryCode(countryCode);
        _setPhoneNumber(phoneNumber);
    }, []);
    const onCallInputChanged = useCallback((e) => {
        let num = toEnglishNumber(e.target.value);
        setValue(num);
    }, []);
    const onEmailInputChanged = useCallback((e) => {
        setValue(e.target.value);
    }, []);

    const inputBox = useMemo(() => {
        switch (pageData.eventType) {
            case "email":
                return (
                    <input className="input-box" placeholder={placeholder}
                        value={value} onChange={onEmailInputChanged} dir="ltr"></input>
                );
            case "sms":
                return (
                    <PhoneNumberInput
                        onPhoneNumberChange={onSMSInputChanged}
                    />
                    // <div className="get-number-grp" dir="ltr">
                    //     <div className="box country-box"><div>+98</div> <IconFlagIr className="icon" /></div>
                    //     <input className="box input-box" type="tel" placeholder="912 345 6789" value={value} onChange={onSMSInputChanged}></input>
                    // </div>
                );
            case "call":
                return (
                    <input className="input-box" type="tel" placeholder={placeholder}
                        value={value} onChange={onCallInputChanged} dir="ltr"></input>
                );
            default:
                return null;
        }
    }, [pageData.eventType, value, placeholder,
        onSMSInputChanged, onCallInputChanged, onEmailInputChanged]);


    return (
        <div className="create-bind-page-content">
            <IconBack className="nav-icon" /> <div className="nav-button" onClick={() => { back(); }} />
            <IconX className="nav-icon-secondary" /> <div className="nav-button-secondary" onClick={dismissHandler} />

            <div className="header-box">
                <div>{header}</div>
                {React.createElement(headerIcon, { className: "header-icon" })}
            </div>
            <div className="info">{info}</div>

            {inputBox}

            {needToShowHelpText ? <div className="error-label">{helpText}</div> : null}
            <div className="big-button blue-bottom bottom-next-button" onClick={nextClick} style={{ opacity: !needToShowHelpText ? 1 : 0.3 }}>
                <div>{b.next}</div> <IconBackBlack className="icon-180" />
            </div>
        </div>
    );
}
